export type TargetType = 'rupiah' | 'volume';

export type BusinessGoalType = 'baru' | 'berjalan';

export interface MonthlyRevenueData {
  month: number; // 1-12
  revenue_year1: number; // currentYear - 2
  revenue_year2: number; // currentYear - 1
}

export interface BusinessGoalKinerjaData {
  target_type: TargetType;
  monthly_data: MonthlyRevenueData[];
  projection_kinerja_pct: number;
  business_type: 'berjalan';
}

export interface ProjectionMonthlyTargetData {
  month: number; // 1-12
  values: number[]; // aligned with projection_years index
}

export interface BusinessGoalStartupData {
  target_type: TargetType;
  projection_years: number[];
  projection_monthly_data: ProjectionMonthlyTargetData[];
  business_type: 'baru';
}

export const MONTHS = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

export const PROJECTION_YEARS_COUNT = 5;

export function getActualYears(currentYear: number): [number, number] {
  return [currentYear - 2, currentYear - 1];
}

export function getProjectionYears(currentYear: number): number[] {
  return Array.from({ length: PROJECTION_YEARS_COUNT }, (_, i) => currentYear + i);
}

export function calcKinerja(rev1: number, rev2: number): number | null {
  if (rev1 === 0 && rev2 === 0) return null;
  if (rev1 === 0 && rev2 > 0) return 100;
  if (rev1 > 0 && rev2 === 0) return -100;
  if (rev1 == null || rev2 == null) return null;
  return ((rev2 - rev1) / rev1) * 100;
}

export function calcAverageKinerja(monthlyData: MonthlyRevenueData[]): number | null {
  const valid = monthlyData
    .map(d => calcKinerja(d.revenue_year1, d.revenue_year2))
    .filter((v): v is number => v !== null);
  if (valid.length === 0) return null;
  return valid.reduce((acc, v) => acc + v, 0) / valid.length;
}

export function calcProjectionRevenue(baseRevenue: number, pct: number): number {
  return baseRevenue * (1 + pct / 100);
}

export function calcAllProjections(
  monthlyData: MonthlyRevenueData[],
  pct: number,
  projectionYears: number[],
): Record<number, Record<number, number>> {
  // result[year][month] = projected revenue
  const result: Record<number, Record<number, number>> = {};

  for (const year of projectionYears) {
    result[year] = {};
    for (let month = 1; month <= 12; month++) {
      const yearIndex = projectionYears.indexOf(year);
      if (yearIndex === 0) {
        // First projection year: base is revenue_year2 (currentYear-1)
        const monthData = monthlyData.find(d => d.month === month);
        const base = monthData?.revenue_year2 ?? 0;
        result[year][month] = Math.round(calcProjectionRevenue(base, pct));
      } else {
        // Subsequent years: base is previous projection year
        const prevYear = projectionYears[yearIndex - 1];
        const prevRevenue = result[prevYear][month] ?? 0;
        result[year][month] = Math.round(calcProjectionRevenue(prevRevenue, pct));
      }
    }
  }

  return result;
}

export function createEmptyMonthlyData(): MonthlyRevenueData[] {
  return Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    revenue_year1: 0,
    revenue_year2: 0,
  }));
}

export function createEmptyProjectionMonthlyData(projectionYears: number[]): ProjectionMonthlyTargetData[] {
  return Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    values: Array.from({ length: projectionYears.length }, () => 0),
  }));
}

export function isKinerjaData(value: unknown): value is BusinessGoalKinerjaData {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return 'target_type' in obj && 'monthly_data' in obj && Array.isArray(obj.monthly_data);
}

export function isStartupData(value: unknown): value is BusinessGoalStartupData {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'target_type' in obj &&
    'projection_years' in obj &&
    Array.isArray(obj.projection_years) &&
    'projection_monthly_data' in obj &&
    Array.isArray(obj.projection_monthly_data)
  );
}
